/*******************************************************************************
  설  명 : 상담정보 등록/수정
  작성일 : 2020-02-26
  수정일 : 2025-06-17
  수정내용 : 신규등록시 상담일자 / 상담자 기본값 적용 + 비동기 초기화 안정화
  작성자 : 송영석 + 보완 (AI)
*******************************************************************************/
import { Component, OnInit, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateStruct, NgbActiveModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';

import { UtilService } from '@app/service/util.service';
import { CustomValidator } from '@app/service/custom.validators';
import { MemberService } from '@app/service/member.service';
import { CommonCodeService } from '@app/service/common.code.service';
import { ReceptionService } from '@app/service/reception.service';
import { Globals } from '@app/service/globals.service';

import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';


@Component({
  selector: 'app-modal-counseling-add',
  templateUrl: './modal-counseling-add.component.html',
  styleUrls: ['./modal-counseling-add.component.scss']
})
export class ModalCounselingAddComponent implements OnInit {

  /*******************************************************************************
    전역변수 및 부모로부터 전달받는 값 정의
  *******************************************************************************/
  @Input() counselingSeq: any;
  @Input() receptionSeq: any;
  @Input() facilityCode: any;

  // 신규등록 시 기본값 세팅을 위한 값
  @Input() defaultCounselingDate: string;
  @Input() defaultMemberNo: string;

  public typeList: any = [];      // 상담구분 select 리스트
  public memberList: any = [];    // 상담자 select 리스트

  public title: any = '등록';     // 모달 타이틀
  private dateModel: NgbDateStruct;

  public form: FormGroup;
  public formErrors = {};

  /*******************************************************************************
    생성자 : FormBuilder 및 서비스 주입
  *******************************************************************************/
  constructor(
    private utilService: UtilService,
    private toastrService: ToastrService,
    private router: Router,
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private globals: Globals,
    private memberService: MemberService,
    private commonCodeService: CommonCodeService,
    private receptionService: ReceptionService,
    public activeModal: NgbActiveModal,
  ) {
    this.buildForm();
  }

  /*******************************************************************************
    FormGroup 생성 및 유효성 설정
  *******************************************************************************/
  buildForm(): void {
    this.form = this.formBuilder.group({
      counselingSeq:["", [] ],
      receptionSeq:["", [Validators.required] ],
      facilityCode:["", [Validators.required] ],
      counselingDate:["", [Validators.required] ],
      counselingType:["", [Validators.required] ],
      memberNo:["", [Validators.required] ],
      counselingMemo:["", [Validators.required] ],
    });

    // Form 값 변경시 에러메시지 갱신
    this.form.valueChanges.subscribe(data => {
      this.utilService.updateFormErrors(this.form, this.formErrors);
    });
  };

  /*******************************************************************************
    ngOnInit : 초기 데이터 로딩
  *******************************************************************************/
  async ngOnInit() {
    // 기본값 form에 세팅 (counselingSeq, receptionSeq, facilityCode)
    this.form.patchValue({
      counselingSeq: this.counselingSeq,
      receptionSeq: this.receptionSeq,
      facilityCode: this.facilityCode
    });

    // 공통코드 및 상담자 리스트 비동기로 순차 로딩
    await this.getCommonList();

    // 수정모드
    if (this.counselingSeq != false && this.counselingSeq != '0') {
      this.title = '수정';
      await this.getReceptionCounselingInfo();
    }
    // 신규등록시 기본값 세팅
    else {
      this.setDefaultValues();
    }
  }

  /*******************************************************************************
    공통코드 및 상담자 리스트 가져오기
  *******************************************************************************/
  async getCommonList() {
    // 상담구분 리스트
    const counselingRes = await this.commonCodeService.getCounselingTypeSelect();
    if (counselingRes.ResultCode) {
      this.typeList = counselingRes.data.map(row => ({
        title: row.commonName,
        value: row.commonCode
      }));
    } else {
      this.typeList = [];
    }

    // 상담자 리스트
    const memberRes = await this.memberService.getMemberSelect();
    if (memberRes.ResultCode) {
      this.memberList = memberRes.data.map(row => ({
        title: row.name,
        value: String(row.memberNo)
      }));

    } else {
      this.memberList = [];
    }
  }

  /*******************************************************************************
    신규등록시 기본값 세팅
  *******************************************************************************/
  setDefaultValues() {
  // 상담일자 먼저 적용 (기존 그대로 유지)
  if (this.defaultCounselingDate) {
    const date = moment(this.defaultCounselingDate, 'YYYY-MM-DD');
    this.dateModel = {
      year: date.year(),
      month: date.month() + 1,
      day: date.date()
    };
    this.form.patchValue({ counselingDate: this.dateModel });
  }

  // 상담자 값은 약간 타이밍을 늦춰준다
  if (this.defaultMemberNo) {
    setTimeout(() => {
      this.form.patchValue({ memberNo: this.defaultMemberNo });
    }, 0);
  }
  }

  /*******************************************************************************
    상담 상세조회 (수정모드)
  *******************************************************************************/
  async getReceptionCounselingInfo() {
    const response = await this.receptionService.getReceptionCounselingInfo(this.counselingSeq);
    if (response.ResultCode) {
      let counselingInfo = response.data;

      // 상담일자 변환
      if (counselingInfo.counselingDate !== null) {
        this.dateModel = {
          year: parseInt(counselingInfo.counselingDate.substr(0, 4)),
          month: parseInt(counselingInfo.counselingDate.substr(5, 2)),
          day: parseInt(counselingInfo.counselingDate.substr(8, 2))
        };
        counselingInfo.counselingDate = this.dateModel;
      }

      this.form.patchValue(counselingInfo);
    } else {
      this.toastrService.error(response.ResultMessage, '');
    }
  }

  /*******************************************************************************
    상담 등록/수정 저장 처리
  *******************************************************************************/
  setCounseling() {
    this.utilService.makeFormDirtyAndUpdateErrors(this.form, this.formErrors);

    if (this.form.valid) {
      this.receptionService.setCounseling(this.form).then(response => {
        if (response.ResultCode) {
          this.toastrService.success(response.ResultMessage, '');
          this.activeModal.close(true);
        } else {
          this.toastrService.error(response.ResultMessage, '');
        }
      });
    } else {
      this.toastrService.error('필수 입력항목을 확인하시기 바랍니다.', '');
    }
  }

  /*******************************************************************************
    상담 삭제처리
  *******************************************************************************/
  setCounselingDelete() {
    if (confirm("해당 상담을 삭제하시겠습니까?")) {
      this.receptionService.setCounselingDelete(this.counselingSeq).then(response => {
        if (response.ResultCode) {
          this.toastrService.success(response.ResultMessage, '');
          this.activeModal.close(true);
        } else {
          this.toastrService.error(response.ResultMessage, '');
        }
      }, error => {
        this.toastrService.error(error, '');
        this.activeModal.close();
      });
    }
  }
}
